/**
 * CompareTab — Period-over-Period comparative analytics
 *
 * Design inspiration: Toast POS "Compare" view, Square Analytics,
 * Revel Systems comparative dashboard.
 *
 * Features:
 *  • Compare mode selector: Mon vs Mon | Week vs Week | Month vs Month | Custom
 *  • Dual-fetch: current period + comparison period
 *  • KPI delta cards with trend arrows (green/red)
 *  • Hourly revenue heatmap (time-of-day breakdown)
 *  • Afternoon performance deep-dive (11am–3pm window)
 *  • Side-by-side category comparison bars
 *  • Top movers — biggest gainers and fallers
 *  • AI-style insight callouts (rule-based pattern detection)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fmt$, fmtN } from '../../../utils/formatUtils';
import { toCST_ISO, tzDate } from '../../../utils/cstUtils';
import { API_BASE, computeRange, isoDate } from '../squareDashboardUtils';

// ─── Compare period helpers ───────────────────────────────────────────────────

/** Get this current week's Monday. */
function thisMonday(referenceNow = new Date()) {
  const d = tzDate(referenceNow);
  const dow = d.getDay();
  const daysBack = dow === 0 ? 6 : dow - 1;
  d.setDate(d.getDate() - daysBack);
  return d;
}

const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const _todayDow = tzDate(new Date()).getDay();
const _dayName  = DAY_NAMES[_todayDow];

export const COMPARE_MODES = [
  { key: 'day',   label: `📅 ${_dayName} vs ${_dayName}`,   desc: `This ${_dayName} vs last ${_dayName}` },
  { key: 'week',  label: '📆 Week vs Week',    desc: 'This week vs last week' },
  { key: 'month', label: '🗓 Month vs Month',  desc: 'This month vs last month' },
  { key: 'custom',label: '✏️ Custom',           desc: 'Pick your own dates' },
];

/** Given a mode, return { current: {start,end}, prior: {start,end} } Date pairs. */
export function getComparePeriods(mode, customA, customB) {
  const now = new Date();
  const today = tzDate(now);

  if (mode === 'day' || mode === 'mon') {
    // Compare today vs the same weekday last week
    const thisDay  = new Date(today); thisDay.setHours(0, 0, 0, 0);
    const lastDay  = new Date(thisDay); lastDay.setDate(lastDay.getDate() - 7);
    const makeEnd  = d => { const e = new Date(d); e.setHours(23,59,59,999); return e; };
    const dow      = thisDay.getDay();
    const name     = DAY_NAMES[dow];
    return {
      current: { start: thisDay,          end: makeEnd(thisDay) },
      prior:   { start: lastDay,          end: makeEnd(lastDay) },
      currentLabel: `This ${name} (${isoDate(thisDay)})`,
      priorLabel:   `Last ${name} (${isoDate(lastDay)})`,
    };
  }
  if (mode === 'week') {
    const weekStart = thisMonday(now);
    const prevStart = new Date(weekStart); prevStart.setDate(prevStart.getDate() - 7);
    const prevEnd   = new Date(weekStart); prevEnd.setDate(prevEnd.getDate() - 1); prevEnd.setHours(23,59,59,999);
    const currEnd   = new Date(today); currEnd.setHours(23,59,59,999);
    return {
      current: { start: weekStart, end: currEnd  },
      prior:   { start: prevStart, end: prevEnd  },
      currentLabel: `This Week (${isoDate(weekStart)}–${isoDate(today)})`,
      priorLabel:   `Last Week (${isoDate(prevStart)}–${isoDate(prevEnd)})`,
    };
  }
  if (mode === 'month') {
    const currStart = new Date(today); currStart.setDate(1);
    const prevStart = new Date(currStart); prevStart.setMonth(prevStart.getMonth() - 1);
    const prevEnd   = new Date(currStart); prevEnd.setDate(0); prevEnd.setHours(23,59,59,999);
    const currEnd   = new Date(today); currEnd.setHours(23,59,59,999);
    return {
      current: { start: currStart, end: currEnd },
      prior:   { start: prevStart, end: prevEnd },
      currentLabel: currStart.toLocaleString('default', { month: 'long' }),
      priorLabel:   prevStart.toLocaleString('default', { month: 'long' }),
    };
  }
  // custom
  const aStart = customA?.start || computeRange('today').start;
  const aEnd   = customA?.end   || computeRange('today').end;
  const bStart = customB?.start || computeRange('yesterday').start;
  const bEnd   = customB?.end   || computeRange('yesterday').end;
  return {
    current: { start: aStart, end: aEnd },
    prior:   { start: bStart, end: bEnd },
    currentLabel: `Period A`,
    priorLabel:   `Period B`,
  };
}

// ─── Hourly bucketing ─────────────────────────────────────────────────────────
const HOUR_SLOTS = Array.from({ length: 14 }, (_, i) => i + 9); // 9am–10pm

function parseTicketHour(ticket) {
  // API returns ticket.time (not createdAt)
  const raw = ticket.time || ticket.createdAt;
  if (!raw) return null;
  const d = new Date(raw);
  // Convert to local (Chicago) hour
  const h = parseInt(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    hour: '2-digit',
    hour12: false,
  }).format(d), 10);
  return h;
}

function buildHourlyBuckets(data) {
  const buckets = {};
  HOUR_SLOTS.forEach(h => { buckets[h] = { tickets: 0, revenueCents: 0 }; });
  if (!data?.categories) return buckets;
  data.categories.forEach(cat => {
    (cat.ticketList || []).forEach(tkt => {
      const h = parseTicketHour(tkt);
      if (h !== null && buckets[h] !== undefined) {
        buckets[h].tickets++;
        buckets[h].revenueCents += tkt.revenueCents || 0;
      }
    });
  });
  return buckets;
}

/** 11am–3pm = "lunch/afternoon" window as used by Toast & Square analytics */
const AFTERNOON_HOURS = [11, 12, 13, 14, 15];

function afternoonStats(hourlyBuckets) {
  return AFTERNOON_HOURS.reduce((acc, h) => {
    const b = hourlyBuckets[h] || { tickets: 0, revenueCents: 0 };
    acc.tickets      += b.tickets;
    acc.revenueCents += b.revenueCents;
    return acc;
  }, { tickets: 0, revenueCents: 0 });
}

// ─── Delta helpers ────────────────────────────────────────────────────────────
function delta(curr, prior) {
  if (!prior || prior === 0) return null;
  return ((curr - prior) / Math.abs(prior)) * 100;
}

function fmtDelta(pct) {
  if (pct === null) return '—';
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

function deltaClass(pct, invertGood = false) {
  if (pct === null) return '';
  const isGood = invertGood ? pct < 0 : pct >= 0;
  return isGood ? 'cmp__delta--up' : 'cmp__delta--down';
}

// ─── AI-style insight engine ──────────────────────────────────────────────────
function generateInsights(currData, priorData, currLabel, priorLabel, currHourly, priorHourly) {
  const insights = [];
  if (!currData || !priorData) return insights;

  const currRev  = parseFloat(currData.summary?.totalRevenue  || 0);
  const priorRev = parseFloat(priorData.summary?.totalRevenue || 0);
  const revDelta = delta(currRev, priorRev);

  // Revenue trend
  if (revDelta !== null) {
    if (revDelta > 20)
      insights.push({ type: 'positive', icon: '🚀', text: `Revenue is up ${fmtDelta(revDelta)} vs ${priorLabel} — strong performance!` });
    else if (revDelta > 5)
      insights.push({ type: 'positive', icon: '📈', text: `Revenue grew ${fmtDelta(revDelta)} compared to ${priorLabel}.` });
    else if (revDelta < -15)
      insights.push({ type: 'warning', icon: '⚠️', text: `Revenue dropped ${fmtDelta(revDelta)} vs ${priorLabel} — investigate staffing or menu issues.` });
    else if (revDelta < -5)
      insights.push({ type: 'caution', icon: '📉', text: `Revenue is down ${fmtDelta(revDelta)} vs ${priorLabel}.` });
  }

  // Afternoon performance
  const currPM  = afternoonStats(currHourly);
  const priorPM = afternoonStats(priorHourly);
  const pmDelta = delta(currPM.revenueCents, priorPM.revenueCents);
  if (pmDelta !== null && Math.abs(pmDelta) > 10) {
    if (pmDelta > 0)
      insights.push({ type: 'positive', icon: '☀️', text: `Afternoon (11am–3pm) revenue is up ${fmtDelta(pmDelta)} — lunch rush is stronger this period.` });
    else
      insights.push({ type: 'caution', icon: '🕐', text: `Afternoon (11am–3pm) revenue dropped ${fmtDelta(pmDelta)}. Consider a lunch special or promo.` });
  }

  // Best hour shift
  const currBest  = HOUR_SLOTS.reduce((best, h) => (currHourly[h]?.revenueCents  > (currHourly[best]?.revenueCents  || 0) ? h : best), 9);
  const priorBest = HOUR_SLOTS.reduce((best, h) => (priorHourly[h]?.revenueCents > (priorHourly[best]?.revenueCents || 0) ? h : best), 9);
  if (currBest !== priorBest) {
    const fmt12 = h => `${h > 12 ? h - 12 : h}${h >= 12 ? 'pm' : 'am'}`;
    insights.push({ type: 'info', icon: '⏰', text: `Peak hour shifted from ${fmt12(priorBest)} to ${fmt12(currBest)}.` });
  }

  // Ticket volume
  const currTix  = currData.summary?.totalTickets  || 0;
  const priorTix = priorData.summary?.totalTickets || 0;
  const tixDelta = delta(currTix, priorTix);
  if (tixDelta !== null && Math.abs(tixDelta) > 15) {
    if (tixDelta > 0)
      insights.push({ type: 'positive', icon: '🎫', text: `${fmtDelta(tixDelta)} more tickets than ${priorLabel} — higher foot traffic.` });
    else
      insights.push({ type: 'caution', icon: '🎫', text: `${fmtDelta(tixDelta)} fewer tickets than ${priorLabel}.` });
  }

  // Discount rate
  const currDiscRate  = currRev  > 0 ? parseFloat(currData.summary?.totalDiscount  || 0) / currRev  : 0;
  const priorDiscRate = priorRev > 0 ? parseFloat(priorData.summary?.totalDiscount || 0) / priorRev : 0;
  if (currDiscRate - priorDiscRate > 0.05)
    insights.push({ type: 'caution', icon: '🏷️', text: `Discount rate increased to ${(currDiscRate*100).toFixed(1)}% (was ${(priorDiscRate*100).toFixed(1)}%). Check promo impact on margins.` });

  // Top category winner/loser
  const currCatMap  = Object.fromEntries((currData.categories  || []).map(c => [c.name, c.revenueCents]));
  const priorCatMap = Object.fromEntries((priorData.categories || []).map(c => [c.name, c.revenueCents]));
  const commonCats  = Object.keys(currCatMap).filter(k => priorCatMap[k] > 0);
  if (commonCats.length > 0) {
    const deltas = commonCats.map(k => ({ name: k, d: delta(currCatMap[k], priorCatMap[k]) })).filter(x => x.d !== null);
    const winner = deltas.reduce((a, b) => (b.d > a.d ? b : a), deltas[0]);
    const loser  = deltas.reduce((a, b) => (b.d < a.d ? b : a), deltas[0]);
    if (winner && winner.d > 15)
      insights.push({ type: 'positive', icon: '🏆', text: `"${winner.name}" is the top growing category at ${fmtDelta(winner.d)} revenue increase.` });
    if (loser && loser.d < -15)
      insights.push({ type: 'warning', icon: '📦', text: `"${loser.name}" category revenue fell ${fmtDelta(loser.d)} — review item performance.` });
  }

  return insights.slice(0, 6); // cap at 6
}

// ─── Fetch hook ───────────────────────────────────────────────────────────────
function usePeriodData(start, end) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const fetch_ = useCallback(async () => {
    if (!start || !end) return;
    setLoading(true);
    setError(null);
    try {
      const url = `${API_BASE}/api/square-sales?startAt=${encodeURIComponent(toCST_ISO(start))}&endAt=${encodeURIComponent(toCST_ISO(end))}`;
      const res  = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || 'Error');
      setData(json);
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [start?.getTime(), end?.getTime()]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetch_(); }, [fetch_]);
  return { data, loading, error, refetch: fetch_ };
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function DeltaKpi({ icon, label, currVal, priorVal, currFmt, priorFmt, invertGood }) {
  const d     = delta(currVal, priorVal);
  const cls   = deltaClass(d, invertGood);
  const arrow = d === null ? '' : d >= 0 ? ' ▲' : ' ▼';
  return (
    <div className="cmp__kpi">
      <div className="cmp__kpi-icon">{icon}</div>
      <div className="cmp__kpi-label">{label}</div>
      <div className="cmp__kpi-curr">{currFmt}</div>
      <div className="cmp__kpi-prior">{priorFmt}</div>
      <div className={`cmp__delta ${cls}`}>{fmtDelta(d)}{arrow}</div>
    </div>
  );
}

function HourlyHeatmap({ currBuckets, priorBuckets, currLabel, priorLabel }) {
  const maxRev = Math.max(
    ...HOUR_SLOTS.map(h => Math.max(
      currBuckets[h]?.revenueCents || 0,
      priorBuckets[h]?.revenueCents || 0
    )), 1
  );
  const fmt12 = h => `${h > 12 ? h - 12 : h}${h >= 12 ? 'pm' : 'am'}`;
  const pct_  = (v, max) => Math.round((v / max) * 100);

  return (
    <div className="cmp__heatmap">
      <div className="cmp__heatmap-legend">
        <span className="cmp__heatmap-dot cmp__heatmap-dot--curr"/>{currLabel}
        <span className="cmp__heatmap-dot cmp__heatmap-dot--prior" style={{marginLeft:16}}/>{priorLabel}
      </div>
      <div className="cmp__heatmap-grid">
        {HOUR_SLOTS.map(h => {
          const curr  = currBuckets[h]  || { tickets: 0, revenueCents: 0 };
          const prior = priorBuckets[h] || { tickets: 0, revenueCents: 0 };
          const isAfternoon = AFTERNOON_HOURS.includes(h);
          return (
            <div key={h} className={`cmp__heatmap-col${isAfternoon ? ' cmp__heatmap-col--pm' : ''}`}>
              <div className="cmp__heatmap-bars">
                <div className="cmp__heatmap-bar-wrap">
                  {curr.revenueCents > 0 && (
                    <span className="cmp__heatmap-val cmp__heatmap-val--curr">{fmt$(curr.revenueCents/100)}</span>
                  )}
                  <div
                    className="cmp__heatmap-bar cmp__heatmap-bar--curr"
                    style={{ height: `${pct_(curr.revenueCents, maxRev)}%` }}
                    title={`${currLabel} ${fmt12(h)}: ${fmt$(curr.revenueCents/100)} · ${curr.tickets} tickets`}
                  />
                </div>
                <div className="cmp__heatmap-bar-wrap">
                  {prior.revenueCents > 0 && (
                    <span className="cmp__heatmap-val cmp__heatmap-val--prior">{fmt$(prior.revenueCents/100)}</span>
                  )}
                  <div
                    className="cmp__heatmap-bar cmp__heatmap-bar--prior"
                    style={{ height: `${pct_(prior.revenueCents, maxRev)}%` }}
                    title={`${priorLabel} ${fmt12(h)}: ${fmt$(prior.revenueCents/100)} · ${prior.tickets} tickets`}
                  />
                </div>
              </div>
              <div className={`cmp__heatmap-lbl${isAfternoon ? ' cmp__heatmap-lbl--pm' : ''}`}>{fmt12(h)}</div>
            </div>
          );
        })}
      </div>
      <div className="cmp__heatmap-pm-label">☀️ Afternoon window (11am–3pm)</div>
    </div>
  );
}

function AfternoonCard({ currBuckets, priorBuckets, currLabel, priorLabel }) {
  const curr  = afternoonStats(currBuckets);
  const prior = afternoonStats(priorBuckets);
  const revD  = delta(curr.revenueCents,  prior.revenueCents);
  const tixD  = delta(curr.tickets,       prior.tickets);
  const avgCurr  = curr.tickets  > 0 ? curr.revenueCents  / curr.tickets  : 0;
  const avgPrior = prior.tickets > 0 ? prior.revenueCents / prior.tickets : 0;

  return (
    <div className="cmp__pm-card">
      <div className="cmp__pm-title">☀️ Afternoon Performance (11am – 3pm)</div>
      <div className="cmp__pm-grid">
        <div className="cmp__pm-col">
          <div className="cmp__pm-period">{currLabel}</div>
          <div className="cmp__pm-rev">{fmt$(curr.revenueCents/100)}</div>
          <div className="cmp__pm-meta">{curr.tickets} tickets · avg {fmt$(avgCurr/100)}/ticket</div>
        </div>
        <div className="cmp__pm-divider">vs</div>
        <div className="cmp__pm-col">
          <div className="cmp__pm-period">{priorLabel}</div>
          <div className="cmp__pm-rev">{fmt$(prior.revenueCents/100)}</div>
          <div className="cmp__pm-meta">{prior.tickets} tickets · avg {fmt$(avgPrior/100)}/ticket</div>
        </div>
        <div className="cmp__pm-deltas">
          <div className={`cmp__pm-delta ${deltaClass(revD)}`}>Revenue {fmtDelta(revD)} {revD !== null ? (revD >= 0 ? '▲' : '▼') : ''}</div>
          <div className={`cmp__pm-delta ${deltaClass(tixD)}`}>Tickets {fmtDelta(tixD)} {tixD !== null ? (tixD >= 0 ? '▲' : '▼') : ''}</div>
        </div>
      </div>
      <div className="cmp__pm-hours">
        {AFTERNOON_HOURS.map(h => {
          const c = currBuckets[h]  || { revenueCents: 0, tickets: 0 };
          const p = priorBuckets[h] || { revenueCents: 0, tickets: 0 };
          const d_ = delta(c.revenueCents, p.revenueCents);
          const fmt12 = h => `${h > 12 ? h-12 : h}${h >= 12 ? 'pm' : 'am'}`;
          return (
            <div key={h} className="cmp__pm-hour">
              <div className="cmp__pm-hour-label">{fmt12(h)}–{fmt12(h+1)}</div>
              <div className="cmp__pm-hour-curr">{fmt$(c.revenueCents/100)}</div>
              <div className="cmp__pm-hour-prior">{fmt$(p.revenueCents/100)}</div>
              <div className={`cmp__pm-hour-delta ${deltaClass(d_)}`}>{fmtDelta(d_)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CategoryCompare({ currData, priorData, currLabel, priorLabel }) {
  const currMap  = Object.fromEntries((currData?.categories  || []).map(c => [c.name, c]));
  const priorMap = Object.fromEntries((priorData?.categories || []).map(c => [c.name, c]));
  const allCats  = [...new Set([
    ...(currData?.categories  || []).map(c => c.name),
    ...(priorData?.categories || []).map(c => c.name),
  ])];
  const maxRev = Math.max(
    ...allCats.map(n => Math.max(
      currMap[n]?.revenueCents  || 0,
      priorMap[n]?.revenueCents || 0
    )), 1
  );
  const sorted = allCats
    .map(n => ({ name: n, curr: currMap[n]?.revenueCents || 0, prior: priorMap[n]?.revenueCents || 0 }))
    .sort((a, b) => (b.curr + b.prior) - (a.curr + a.prior))
    .slice(0, 12);

  return (
    <div className="cmp__cats">
      <div className="cmp__cats-header">
        <span className="cmp__cats-leg cmp__cats-leg--curr">{currLabel}</span>
        <span className="cmp__cats-leg cmp__cats-leg--prior">{priorLabel}</span>
      </div>
      {sorted.map(({ name, curr, prior }) => {
        const d_ = delta(curr, prior);
        return (
          <div key={name} className="cmp__cats-row">
            <div className="cmp__cats-name">{name}</div>
            <div className="cmp__cats-bars">
              <div className="cmp__cats-bar-wrap">
                <div className="cmp__cats-bar cmp__cats-bar--curr" style={{ width: `${Math.round((curr/maxRev)*100)}%` }} />
                <span className="cmp__cats-bar-val">{fmt$(curr/100)}</span>
              </div>
              <div className="cmp__cats-bar-wrap">
                <div className="cmp__cats-bar cmp__cats-bar--prior" style={{ width: `${Math.round((prior/maxRev)*100)}%` }} />
                <span className="cmp__cats-bar-val">{fmt$(prior/100)}</span>
              </div>
            </div>
            <div className={`cmp__cats-delta ${deltaClass(d_)}`}>{fmtDelta(d_)}</div>
          </div>
        );
      })}
    </div>
  );
}

function TopMovers({ currData, priorData, currLabel, priorLabel }) {
  const currMap  = {};
  const priorMap = {};
  (currData?.categories  || []).forEach(cat => cat.items?.forEach(it => {
    currMap[it.name]  = (currMap[it.name]  || 0) + it.itemQty;
  }));
  (priorData?.categories || []).forEach(cat => cat.items?.forEach(it => {
    priorMap[it.name] = (priorMap[it.name] || 0) + it.itemQty;
  }));
  const allItems = [...new Set([...Object.keys(currMap), ...Object.keys(priorMap)])];
  const withDelta = allItems
    .map(n => ({ name: n, curr: currMap[n] || 0, prior: priorMap[n] || 0, d: delta(currMap[n] || 0, priorMap[n] || 0) }))
    .filter(x => x.d !== null && (x.curr > 0 || x.prior > 0));

  const gainers = [...withDelta].sort((a, b) => b.d - a.d).slice(0, 5);
  const fallers = [...withDelta].sort((a, b) => a.d - b.d).slice(0, 5);

  return (
    <div className="cmp__movers">
      <div className="cmp__movers-col">
        <div className="cmp__movers-title cmp__movers-title--up">🏆 Top Gainers</div>
        {gainers.map(item => (
          <div key={item.name} className="cmp__mover-row">
            <span className="cmp__mover-name">{item.name}</span>
            <span className="cmp__mover-qty">{item.curr} <span className="cmp__mover-vs">vs {item.prior}</span></span>
            <span className="cmp__delta cmp__delta--up">{fmtDelta(item.d)} ▲</span>
          </div>
        ))}
      </div>
      <div className="cmp__movers-col">
        <div className="cmp__movers-title cmp__movers-title--down">📉 Top Fallers</div>
        {fallers.map(item => (
          <div key={item.name} className="cmp__mover-row">
            <span className="cmp__mover-name">{item.name}</span>
            <span className="cmp__mover-qty">{item.curr} <span className="cmp__mover-vs">vs {item.prior}</span></span>
            <span className="cmp__delta cmp__delta--down">{fmtDelta(item.d)} ▼</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Custom period pickers ────────────────────────────────────────────────────
function CustomPicker({ label, start, end, onStart, onEnd }) {
  return (
    <div className="cmp__custom-picker">
      <div className="cmp__custom-label">{label}</div>
      <div className="cmp__custom-inputs">
        <input type="datetime-local" className="cmp__dt-input" value={start} onChange={e => onStart(e.target.value)} />
        <span className="cmp__custom-to">→</span>
        <input type="datetime-local" className="cmp__dt-input" value={end}   onChange={e => onEnd(e.target.value)} />
      </div>
    </div>
  );
}

// ─── Main CompareTab ──────────────────────────────────────────────────────────
export default function CompareTab() {
  const [mode, setMode] = useState('day');

  // Custom date states
  const [customAStart, setCustomAStart] = useState('');
  const [customAEnd,   setCustomAEnd]   = useState('');
  const [customBStart, setCustomBStart] = useState('');
  const [customBEnd,   setCustomBEnd]   = useState('');

  // Seed custom fields
  useEffect(() => {
    const today = computeRange('today');
    const yest  = computeRange('yesterday');
    const toInput = d => {
      const pad = n => String(n).padStart(2,'0');
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    setCustomAStart(toInput(today.start));
    setCustomAEnd(toInput(today.end));
    setCustomBStart(toInput(yest.start));
    setCustomBEnd(toInput(yest.end));
  }, []);

  const periods = useMemo(() => {
    if (mode === 'custom') {
      return getComparePeriods('custom',
        customAStart && customAEnd ? { start: new Date(customAStart), end: new Date(customAEnd) } : null,
        customBStart && customBEnd ? { start: new Date(customBStart), end: new Date(customBEnd) } : null,
      );
    }
    return getComparePeriods(mode);
  }, [mode, customAStart, customAEnd, customBStart, customBEnd]);

  const { data: currData, loading: currLoading, error: currError } =
    usePeriodData(periods.current.start, periods.current.end);
  const { data: priorData, loading: priorLoading, error: priorError } =
    usePeriodData(periods.prior.start, periods.prior.end);

  const currHourly  = useMemo(() => buildHourlyBuckets(currData),  [currData]);
  const priorHourly = useMemo(() => buildHourlyBuckets(priorData), [priorData]);

  const insights = useMemo(() =>
    generateInsights(currData, priorData, periods.currentLabel, periods.priorLabel, currHourly, priorHourly),
    [currData, priorData, periods, currHourly, priorHourly]
  );

  const loading = currLoading || priorLoading;
  const error   = currError || priorError;

  // KPI values
  const currRev    = parseFloat(currData?.summary?.totalRevenue  || 0);
  const priorRev   = parseFloat(priorData?.summary?.totalRevenue || 0);
  const currTix    = currData?.summary?.totalTickets  || 0;
  const priorTix   = priorData?.summary?.totalTickets || 0;
  const currItems  = (currData?.categories  || []).reduce((s,c) => s + c.itemQty, 0);
  const priorItems = (priorData?.categories || []).reduce((s,c) => s + c.itemQty, 0);
  const currDisc   = parseFloat(currData?.summary?.totalDiscount  || 0);
  const priorDisc  = parseFloat(priorData?.summary?.totalDiscount || 0);
  const currAvg    = currTix  > 0 ? (currRev  / currTix)  : 0;
  const priorAvg   = priorTix > 0 ? (priorRev / priorTix) : 0;

  return (
    <div className="cmp__wrap">
      {/* ── Mode selector ── */}
      <div className="cmp__mode-bar">
        {COMPARE_MODES.map(m => (
          <button
            key={m.key}
            className={`cmp__mode-btn${mode === m.key ? ' cmp__mode-btn--active' : ''}`}
            onClick={() => setMode(m.key)}
            title={m.desc}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* ── Custom pickers (only shown in custom mode) ── */}
      {mode === 'custom' && (
        <div className="cmp__custom-row">
          <CustomPicker label="Period A (current)" start={customAStart} end={customAEnd} onStart={setCustomAStart} onEnd={setCustomAEnd} />
          <CustomPicker label="Period B (compare to)" start={customBStart} end={customBEnd} onStart={setCustomBStart} onEnd={setCustomBEnd} />
        </div>
      )}

      {/* ── Period labels ── */}
      <div className="cmp__period-strip">
        <span className="cmp__period-tag cmp__period-tag--curr">📌 {periods.currentLabel}</span>
        <span className="cmp__period-vs">vs</span>
        <span className="cmp__period-tag cmp__period-tag--prior">📌 {periods.priorLabel}</span>
      </div>

      {/* ── Loading / error ── */}
      {loading && (
        <div className="cmp__loading">
          <span className="sq__spinner" /> Fetching both periods…
        </div>
      )}
      {error && !loading && (
        <div className="cmp__error">⚠️ {error}</div>
      )}

      {/* ── Main content ── */}
      {currData && priorData && !loading && (
        <>
          {/* Insights */}
          {insights.length > 0 && (
            <div className="cmp__insights">
              <div className="cmp__insights-title">💡 Insights</div>
              <div className="cmp__insights-list">
                {insights.map((ins, i) => (
                  <div key={i} className={`cmp__insight cmp__insight--${ins.type}`}>
                    <span className="cmp__insight-icon">{ins.icon}</span>
                    <span className="cmp__insight-text">{ins.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* KPI delta cards */}
          <div className="cmp__kpis">
            <DeltaKpi icon="💵" label="Revenue" currVal={currRev} priorVal={priorRev}
              currFmt={fmt$(currRev)} priorFmt={fmt$(priorRev)} />
            <DeltaKpi icon="🎫" label="Tickets" currVal={currTix} priorVal={priorTix}
              currFmt={fmtN(currTix)} priorFmt={fmtN(priorTix)} />
            <DeltaKpi icon="🛒" label="Items Sold" currVal={currItems} priorVal={priorItems}
              currFmt={fmtN(currItems)} priorFmt={fmtN(priorItems)} />
            <DeltaKpi icon="📊" label="Avg/Ticket" currVal={currAvg} priorVal={priorAvg}
              currFmt={fmt$(currAvg)} priorFmt={fmt$(priorAvg)} />
            <DeltaKpi icon="🏷️" label="Discounts" currVal={currDisc} priorVal={priorDisc}
              currFmt={fmt$(currDisc)} priorFmt={fmt$(priorDisc)} invertGood />
          </div>

          {/* Hourly heatmap */}
          <div className="cmp__section">
            <div className="cmp__section-title">⏱ Revenue by Hour</div>
            <HourlyHeatmap
              currBuckets={currHourly} priorBuckets={priorHourly}
              currLabel={periods.currentLabel} priorLabel={periods.priorLabel}
            />
          </div>

          {/* Afternoon deep-dive */}
          <div className="cmp__section">
            <AfternoonCard
              currBuckets={currHourly} priorBuckets={priorHourly}
              currLabel={periods.currentLabel} priorLabel={periods.priorLabel}
            />
          </div>

          {/* Category comparison */}
          <div className="cmp__section">
            <div className="cmp__section-title">📁 Category Comparison</div>
            <CategoryCompare
              currData={currData} priorData={priorData}
              currLabel={periods.currentLabel} priorLabel={periods.priorLabel}
            />
          </div>

          {/* Top movers */}
          <div className="cmp__section">
            <div className="cmp__section-title">🔀 Item Movement</div>
            <TopMovers
              currData={currData} priorData={priorData}
              currLabel={periods.currentLabel} priorLabel={periods.priorLabel}
            />
          </div>
        </>
      )}
    </div>
  );
}
